
import React from "react";
import { Transaction } from "@/types";
import { formatCurrency } from "@/lib/utils";
import { format } from "date-fns";
import { ArrowDown, ArrowUp, Trash2, ArrowLeftRight } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { deleteTransaction } from "@/lib/firebase";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

interface TransactionListProps {
  transactions: Transaction[];
  isAdmin?: boolean;
  /** ID of the most-recent transaction for this user (across all tabs/filters). */
  latestTransactionId?: string;
  onTransactionDeleted?: () => void;
}

export function TransactionList({ 
  transactions, 
  isAdmin = false,
  latestTransactionId,
  onTransactionDeleted 
}: TransactionListProps) {
  const { toast } = useToast();

  if (transactions.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground">No transactions yet.</p>
      </div>
    );
  }

  // Helper function to safely format dates
  const formatDate = (dateValue: any) => {
    try {
      // Check if we're dealing with a Firestore timestamp
      if (dateValue && typeof dateValue === 'object' && 'seconds' in dateValue) {
        return format(new Date(dateValue.seconds * 1000), "MMM d, yyyy");
      }
      
      // Check if it's a date string
      if (dateValue && typeof dateValue === 'string') {
        return format(new Date(dateValue), "MMM d, yyyy");
      }
      
      // Return a fallback value if date is invalid
      return "Date unavailable";
    } catch (error) {
      console.error("Error formatting date:", error, dateValue);
      return "Date unavailable";
    }
  };

  // Helper function to format transfer descriptions consistently
  const formatTransactionDescription = (transaction: Transaction) => {
    const description = transaction.description;
    
    // Check if it's a transfer transaction
    if (description.startsWith("Transfer from ") || description.startsWith("Transfer to ")) {
      // Extract the transfer direction and name
      const isFrom = description.startsWith("Transfer from ");
      const prefix = isFrom ? "Transfer from " : "Transfer to ";
      const nameAndDescription = description.substring(prefix.length);
      
      // Check if there's additional description after the name
      const parts = nameAndDescription.split(" - ");
      const name = parts[0];
      const additionalDescription = parts.length > 1 ? parts.slice(1).join(" - ") : "";
      
      return {
        isTransfer: true,
        transferDirection: isFrom ? "from" : "to",
        transferName: name,
        additionalDescription: additionalDescription,
        displayText: additionalDescription 
          ? `${prefix}${name} - ${additionalDescription}`
          : `${prefix}${name}`
      };
    }
    
    // For regular transactions, just return the description
    return {
      isTransfer: false,
      transferDirection: "",
      transferName: "",
      additionalDescription: "",
      displayText: description
    };
  };

  const handleDeleteTransaction = async (transaction: Transaction) => {
    try {
      await deleteTransaction(transaction.id, transaction.userId);
      toast({
        title: "Transaction deleted",
        description: "The transaction has been successfully removed.",
      });
      if (onTransactionDeleted) {
        onTransactionDeleted();
      }
    } catch (error) {
      console.error("Error deleting transaction:", error);
      toast({
        title: "Error",
        description: "Failed to delete transaction. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-4">
      {transactions.map((transaction) => {
        const transactionInfo = formatTransactionDescription(transaction);
        
        const isLatest = transaction.id === latestTransactionId;

        return (
          <div key={transaction.id} className="transaction-item rounded-lg border p-4 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className={`p-2 rounded-full ${
                transactionInfo.isTransfer 
                  ? 'bg-blue-100' 
                  : transaction.amount >= 0 
                    ? 'bg-green-100' 
                    : 'bg-red-100'
              }`}>
                {transactionInfo.isTransfer ? (
                  <ArrowLeftRight className="h-5 w-5 text-blue-600" />
                ) : transaction.amount >= 0 ? (
                  <ArrowDown className="h-5 w-5 text-secondary" />
                ) : (
                  <ArrowUp className="h-5 w-5 text-destructive" />
                )}
              </div>
              <div>
                <p className="font-medium">{transactionInfo.displayText}</p>
                <p className="text-sm text-muted-foreground">
                  {formatDate(transaction.createdAt)}
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <div
                className={`text-lg font-semibold ${
                  transactionInfo.isTransfer
                    ? transaction.amount >= 0 
                      ? "text-blue-600" 
                      : "text-blue-800"
                    : transaction.amount >= 0 
                      ? "transaction-deposit text-secondary-foreground" 
                      : "transaction-withdrawal text-destructive"
                }`}
              >
                {transaction.amount >= 0 ? "+" : ""}
                {formatCurrency(transaction.amount)}
              </div>
              
              {isAdmin && isLatest && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="ghost" size="icon" className="text-destructive hover:bg-destructive/10">
                      <Trash2 className="h-5 w-5" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete Transaction</AlertDialogTitle>
                      <AlertDialogDescription>
                        Are you sure you want to delete this transaction? This will also update the account balance.
                        This action cannot be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction 
                        onClick={() => handleDeleteTransaction(transaction)}
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      >
                        Delete
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
